
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  loadLeagueData,
  saveLeagueData,
  weekSchedule,
  type LeagueData,
} from '../../utils/localStorage';

interface IndividualWinner {
  place: '1st' | '2nd' | '3rd';
  playerName: string;
  netScore: number | string;
  individualPts: number | string;
  teamPts: number | string;
  totalPts: number | string;
  winnings: number | string;
}

interface TeamWinner {
  place: '1st' | '2nd';
  team1Name: string;
  team2Name: string;
  team1Score: number | string;
  team2Score: number | string;
  combinedScore: number | string;
  teamPts: number | string;
  winnings: number | string;
}

interface RoundMoney {
  individuals: IndividualWinner[];
  teams: TeamWinner[];
}

const MONEY_STORAGE_KEY = 'mbc_money_data';

function loadMoneyData(): Record<string, RoundMoney> {
  try {
    const stored = localStorage.getItem(MONEY_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) {
    console.error('Error loading money data:', e);
  }
  return {};
}

function saveMoneyData(data: Record<string, RoundMoney>) {
  localStorage.setItem(MONEY_STORAGE_KEY, JSON.stringify(data));
}

const biWeeklyRounds = [
  { key: '1-2', label: 'Weeks 1 & 2', course: 'Pebble Beach (Front 9)', date: 'Nov 13 - Nov 20, 2025' },
  { key: '3-4', label: 'Weeks 3 & 4', course: 'Panther Lakes (Front 9)', date: 'Dec 4 - Dec 11, 2025' },
  { key: '5-6', label: 'Weeks 5 & 6', course: 'Oslo Golfklubb (Front 9)', date: 'Dec 18, 2025 - Jan 8, 2026' },
  { key: '7-8', label: 'Weeks 7 & 8', course: 'Bay Hill (Front 9)', date: 'Jan 15 - Jan 22, 2026' },
  { key: '9-10', label: 'Weeks 9 & 10', course: 'Banff Springs (Front 9)', date: 'Jan 29 - Feb 5, 2026' },
  { key: '11-12', label: 'Weeks 11 & 12', course: 'St Andrews Old (Front 9)', date: 'Feb 12 - Feb 19, 2026' },
];

export default function MoneyPage() {
  const [moneyData, setMoneyData] = useState<Record<string, RoundMoney>>(loadMoneyData);
  const [selectedRound, setSelectedRound] = useState(biWeeklyRounds[0].key);
  const [showAddIndividual, setShowAddIndividual] = useState(false);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [showPasscode, setShowPasscode] = useState(false);
  const [pendingAction, setPendingAction] = useState<'individual' | 'team' | 'delete' | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'individual' | 'team'; roundKey: string; index: number } | null>(null);

  // Form state for individual
  const [indForm, setIndForm] = useState<IndividualWinner>({
    place: '1st', playerName: '', netScore: '', individualPts: '', teamPts: '', totalPts: '', winnings: ''
  });

  // Form state for team
  const [teamForm, setTeamForm] = useState<TeamWinner>({
    place: '1st', team1Name: '', team2Name: '', team1Score: '', team2Score: '', combinedScore: '', teamPts: '', winnings: ''
  });

  const leagueData = loadLeagueData();
  const players = Object.values(leagueData.players);

  const requestPasscode = (action: 'individual' | 'team' | 'delete') => {
    setPendingAction(action);
    setPasscode('');
    setShowPasscode(true);
  };

  const verifyAndProceed = () => {
    if (passcode === 'mbc909') {
      setIsVerified(true);
      setShowPasscode(false);
      if (pendingAction === 'individual') {
        setIndForm({ place: '1st', playerName: '', netScore: '', individualPts: '', teamPts: '', totalPts: '', winnings: '' });
        setShowAddIndividual(true);
      } else if (pendingAction === 'team') {
        setTeamForm({ place: '1st', team1Name: '', team2Name: '', team1Score: '', team2Score: '', combinedScore: '', teamPts: '', winnings: '' });
        setShowAddTeam(true);
      } else if (pendingAction === 'delete' && deleteTarget) {
        performDelete();
      }
    } else {
      alert('Incorrect passcode');
    }
  };

  const performDelete = () => {
    if (!deleteTarget) return;
    const newData = { ...moneyData };
    const round = newData[deleteTarget.roundKey];
    if (!round) return;
    if (deleteTarget.type === 'individual') {
      round.individuals.splice(deleteTarget.index, 1);
    } else {
      round.teams.splice(deleteTarget.index, 1);
    }
    if (round.individuals.length === 0 && round.teams.length === 0) {
      delete newData[deleteTarget.roundKey];
    }
    saveMoneyData(newData);
    setMoneyData(newData);
    setDeleteTarget(null);
  };

  const handleDeleteClick = (type: 'individual' | 'team', roundKey: string, index: number) => {
    setDeleteTarget({ type, roundKey, index });
    requestPasscode('delete');
  };

  const saveIndividualWinner = () => {
    if (!indForm.playerName) {
      alert('Please enter a player name');
      return;
    }
    const newData = { ...moneyData };
    if (!newData[selectedRound]) {
      newData[selectedRound] = { individuals: [], teams: [] };
    }
    newData[selectedRound].individuals.push({ ...indForm });
    // Sort by place
    newData[selectedRound].individuals.sort((a, b) => {
      const order = { '1st': 1, '2nd': 2, '3rd': 3 };
      return order[a.place] - order[b.place];
    });
    saveMoneyData(newData);
    setMoneyData(newData);
    setShowAddIndividual(false);
    setIsVerified(false);
  };

  const saveTeamWinner = () => {
    if (!teamForm.team1Name || !teamForm.team2Name) {
      alert('Please enter both team member names');
      return;
    }
    const newData = { ...moneyData };
    if (!newData[selectedRound]) {
      newData[selectedRound] = { individuals: [], teams: [] };
    }
    newData[selectedRound].teams.push({ ...teamForm });
    newData[selectedRound].teams.sort((a, b) => {
      const order = { '1st': 1, '2nd': 2 };
      return (order[a.place] || 99) - (order[b.place] || 99);
    });
    saveMoneyData(newData);
    setMoneyData(newData);
    setShowAddTeam(false);
    setIsVerified(false);
  };

  const currentRoundData = moneyData[selectedRound];
  const currentRoundInfo = biWeeklyRounds.find(r => r.key === selectedRound);

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <div className="bg-slate-800 px-6 py-6 border-b border-slate-700 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <Link to="/" className="w-10 h-10 flex items-center justify-center bg-slate-700 rounded-full text-white hover:bg-slate-600 transition-colors">
            <i className="ri-arrow-left-line text-lg"></i>
          </Link>
          <div className="text-center flex-1">
            <h1 className="text-xl font-black text-white">MONEY WINNERS</h1>
            <p className="text-emerald-400 text-xs font-bold">BI-WEEKLY CASH RESULTS</p>
          </div>
          <div className="w-10"></div>
        </div>
      </div>

      {/* Round Selector */}
      <div className="px-6 py-4 bg-slate-800/50">
        <label className="text-slate-400 text-xs font-semibold block mb-2">SELECT ROUND</label>
        <div className="relative">
          <select
            value={selectedRound}
            onChange={(e) => setSelectedRound(e.target.value)}
            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm appearance-none"
          >
            {biWeeklyRounds.map((round) => (
              <option key={round.key} value={round.key}>
                {round.label} - {round.course}
              </option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <i className="ri-arrow-down-s-line text-slate-400"></i>
          </div>
        </div>
        {currentRoundInfo && (
          <div className="mt-2 text-slate-500 text-xs">{currentRoundInfo.date}</div>
        )}
      </div>

      {/* Individual Winners */}
      <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-black text-white">
            <i className="ri-user-line text-emerald-400 mr-2"></i>
            TOP 3 INDIVIDUALS
          </h2>
          <button
            onClick={() => requestPasscode('individual')}
            className="bg-emerald-500 text-white w-9 h-9 rounded-full flex items-center justify-center text-lg font-bold hover:bg-emerald-600 transition-colors"
          >
            +
          </button>
        </div>

        {currentRoundData?.individuals && currentRoundData.individuals.length > 0 ? (
          <div className="space-y-3">
            {currentRoundData.individuals.map((winner, idx) => (
              <div key={idx} className="bg-slate-800 rounded-2xl p-4 border border-slate-700/50 relative">
                <button
                  onClick={() => handleDeleteClick('individual', selectedRound, idx)}
                  className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-300"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`text-2xl ${winner.place === '1st' ? '' : winner.place === '2nd' ? '' : ''}`}>
                    {winner.place === '1st' ? '🥇' : winner.place === '2nd' ? '🥈' : '🥉'}
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-black text-sm">{winner.playerName}</div>
                    <div className="text-slate-400 text-xs">{winner.place} Place</div>
                  </div>
                  <div className="text-right">
                    <div className="text-emerald-400 font-black text-lg">${winner.winnings}</div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 bg-slate-900 rounded-lg p-3">
                  <div className="text-center">
                    <div className="text-slate-500 text-[10px] font-semibold">NET</div>
                    <div className="text-white font-bold text-sm">{winner.netScore || '-'}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-500 text-[10px] font-semibold">IND PTS</div>
                    <div className="text-blue-400 font-bold text-sm">{winner.individualPts || '-'}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-500 text-[10px] font-semibold">TEAM PTS</div>
                    <div className="text-purple-400 font-bold text-sm">{winner.teamPts || '-'}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-500 text-[10px] font-semibold">TOTAL</div>
                    <div className="text-emerald-400 font-bold text-sm">{winner.totalPts || '-'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-slate-800 rounded-2xl p-8 text-center border border-slate-700/50">
            <div className="text-slate-500 text-sm">No individual winners entered yet</div>
            <div className="text-slate-600 text-xs mt-1">Tap + to add winners</div>
          </div>
        )}
      </div>

      {/* Team Winners */}
      <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-black text-white">
            <i className="ri-team-line text-emerald-400 mr-2"></i>
            TOP 2 TEAMS
          </h2>
          <button
            onClick={() => requestPasscode('team')}
            className="bg-emerald-500 text-white w-9 h-9 rounded-full flex items-center justify-center text-lg font-bold hover:bg-emerald-600 transition-colors"
          >
            +
          </button>
        </div>

        {currentRoundData?.teams && currentRoundData.teams.length > 0 ? (
          <div className="space-y-3">
            {currentRoundData.teams.map((team, idx) => (
              <div key={idx} className="bg-slate-800 rounded-2xl p-4 border border-slate-700/50 relative">
                <button
                  onClick={() => handleDeleteClick('team', selectedRound, idx)}
                  className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-300"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
                <div className="flex items-center gap-3 mb-3">
                  <div className="text-2xl">
                    {team.place === '1st' ? '🥇' : '🥈'}
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-black text-sm">{team.team1Name} & {team.team2Name}</div>
                    <div className="text-slate-400 text-xs">{team.place} Place Team</div>
                  </div>
                  <div className="text-right">
                    <div className="text-emerald-400 font-black text-lg">${team.winnings}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 bg-slate-900 rounded-lg p-3 mb-2">
                  <div className="text-center">
                    <div className="text-slate-500 text-[10px] font-semibold">{team.team1Name.split(' ')[0]}</div>
                    <div className="text-white font-bold text-sm">Net: {team.team1Score || '-'}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-500 text-[10px] font-semibold">{team.team2Name.split(' ')[0]}</div>
                    <div className="text-white font-bold text-sm">Net: {team.team2Score || '-'}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 bg-slate-900 rounded-lg p-3">
                  <div className="text-center">
                    <div className="text-slate-500 text-[10px] font-semibold">COMBINED</div>
                    <div className="text-blue-400 font-bold text-sm">{team.combinedScore || '-'}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-500 text-[10px] font-semibold">TEAM PTS</div>
                    <div className="text-purple-400 font-bold text-sm">{team.teamPts || '-'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-slate-800 rounded-2xl p-8 text-center border border-slate-700/50">
            <div className="text-slate-500 text-sm">No team winners entered yet</div>
            <div className="text-slate-600 text-xs mt-1">Tap + to add winning teams</div>
          </div>
        )}
      </div>

      {/* All Rounds Summary */}
      <div className="px-6 py-6">
        <h2 className="text-lg font-black text-white mb-4">
          <i className="ri-history-line text-emerald-400 mr-2"></i>
          ALL ROUNDS
        </h2>
        <div className="space-y-3">
          {biWeeklyRounds.map((round) => {
            const data = moneyData[round.key];
            const hasData = data && (data.individuals.length > 0 || data.teams.length > 0);
            return (
              <button
                key={round.key}
                onClick={() => setSelectedRound(round.key)}
                className={`w-full text-left bg-slate-800 rounded-xl p-4 border transition-all ${
                  selectedRound === round.key
                    ? 'border-emerald-500/50 bg-emerald-500/5'
                    : 'border-slate-700/50 hover:border-slate-600'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-bold text-sm">{round.label}</div>
                    <div className="text-slate-400 text-xs">{round.course}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasData ? (
                      <div className="bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-full text-[10px] font-bold">
                        ENTERED
                      </div>
                    ) : (
                      <div className="bg-slate-700/50 text-slate-500 px-2 py-1 rounded-full text-[10px] font-bold">
                        PENDING
                      </div>
                    )}
                    {selectedRound === round.key && (
                      <i className="ri-checkbox-circle-fill text-emerald-400"></i>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Passcode Modal */}
      {showPasscode && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700">
            <h3 className="text-white font-black text-lg mb-4">Enter Passcode</h3>
            <input
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white mb-4"
              placeholder="Enter passcode"
              onKeyDown={(e) => e.key === 'Enter' && verifyAndProceed()}
            />
            <div className="flex gap-3">
              <button onClick={verifyAndProceed} className="flex-1 bg-emerald-500 text-white py-3 rounded-lg font-bold hover:bg-emerald-600 transition-colors">VERIFY</button>
              <button onClick={() => { setShowPasscode(false); setDeleteTarget(null); }} className="flex-1 bg-slate-700 text-white py-3 rounded-lg font-bold hover:bg-slate-600 transition-colors">CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Individual Winner Modal */}
      {showAddIndividual && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700 max-h-[85vh] overflow-y-auto">
            <h3 className="text-white font-black text-lg mb-1">Add Individual Winner</h3>
            <p className="text-slate-400 text-xs mb-4">{currentRoundInfo?.label} - {currentRoundInfo?.course}</p>

            <div className="space-y-3">
              <div>
                <label className="text-slate-400 text-xs font-semibold block mb-1">Place</label>
                <div className="flex gap-2">
                  {(['1st', '2nd', '3rd'] as const).map(p => (
                    <button
                      key={p}
                      onClick={() => setIndForm({ ...indForm, place: p })}
                      className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                        indForm.place === p ? 'bg-emerald-500 text-white' : 'bg-slate-900 text-slate-400 border border-slate-600'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-slate-400 text-xs font-semibold block mb-1">Player Name</label>
                <input
                  type="text"
                  value={indForm.playerName}
                  onChange={(e) => setIndForm({ ...indForm, playerName: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm"
                  placeholder="e.g. Todd"
                />
              </div>
              <div>
                <label className="text-slate-400 text-xs font-semibold block mb-1">Net Score</label>
                <input
                  type="number"
                  value={indForm.netScore}
                  onChange={(e) => setIndForm({ ...indForm, netScore: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm"
                  placeholder="Net score"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-slate-400 text-[10px] font-semibold block mb-1">IND PTS</label>
                  <input
                    type="number"
                    value={indForm.individualPts}
                    onChange={(e) => setIndForm({ ...indForm, individualPts: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-[10px] font-semibold block mb-1">TEAM PTS</label>
                  <input
                    type="number"
                    value={indForm.teamPts}
                    onChange={(e) => setIndForm({ ...indForm, teamPts: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-[10px] font-semibold block mb-1">TOTAL PTS</label>
                  <input
                    type="number"
                    value={indForm.totalPts}
                    onChange={(e) => setIndForm({ ...indForm, totalPts: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-white text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-slate-400 text-xs font-semibold block mb-1">Winnings ($)</label>
                <input
                  type="number"
                  value={indForm.winnings}
                  onChange={(e) => setIndForm({ ...indForm, winnings: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm"
                  placeholder="e.g. 35"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={saveIndividualWinner} className="flex-1 bg-emerald-500 text-white py-3 rounded-lg font-bold hover:bg-emerald-600 transition-colors">SAVE</button>
                <button onClick={() => { setShowAddIndividual(false); setIsVerified(false); }} className="flex-1 bg-slate-700 text-white py-3 rounded-lg font-bold hover:bg-slate-600 transition-colors">CANCEL</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Team Winner Modal */}
      {showAddTeam && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700 max-h-[85vh] overflow-y-auto">
            <h3 className="text-white font-black text-lg mb-1">Add Team Winner</h3>
            <p className="text-slate-400 text-xs mb-4">{currentRoundInfo?.label} - {currentRoundInfo?.course}</p>

            <div className="space-y-3">
              <div>
                <label className="text-slate-400 text-xs font-semibold block mb-1">Place</label>
                <div className="flex gap-2">
                  {(['1st', '2nd'] as const).map(p => (
                    <button
                      key={p}
                      onClick={() => setTeamForm({ ...teamForm, place: p })}
                      className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                        teamForm.place === p ? 'bg-emerald-500 text-white' : 'bg-slate-900 text-slate-400 border border-slate-600'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-slate-400 text-xs font-semibold block mb-1">Player 1</label>
                  <input
                    type="text"
                    value={teamForm.team1Name}
                    onChange={(e) => setTeamForm({ ...teamForm, team1Name: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-white text-sm"
                    placeholder="Name"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-xs font-semibold block mb-1">Player 2</label>
                  <input
                    type="text"
                    value={teamForm.team2Name}
                    onChange={(e) => setTeamForm({ ...teamForm, team2Name: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-white text-sm"
                    placeholder="Name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-slate-400 text-xs font-semibold block mb-1">P1 Net Score</label>
                  <input
                    type="number"
                    value={teamForm.team1Score}
                    onChange={(e) => setTeamForm({ ...teamForm, team1Score: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-xs font-semibold block mb-1">P2 Net Score</label>
                  <input
                    type="number"
                    value={teamForm.team2Score}
                    onChange={(e) => setTeamForm({ ...teamForm, team2Score: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-white text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-slate-400 text-xs font-semibold block mb-1">Combined Score</label>
                  <input
                    type="number"
                    value={teamForm.combinedScore}
                    onChange={(e) => setTeamForm({ ...teamForm, combinedScore: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-xs font-semibold block mb-1">Team Points</label>
                  <input
                    type="number"
                    value={teamForm.teamPts}
                    onChange={(e) => setTeamForm({ ...teamForm, teamPts: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-white text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-slate-400 text-xs font-semibold block mb-1">Winnings ($)</label>
                <input
                  type="number"
                  value={teamForm.winnings}
                  onChange={(e) => setTeamForm({ ...teamForm, winnings: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm"
                  placeholder="e.g. 50"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={saveTeamWinner} className="flex-1 bg-emerald-500 text-white py-3 rounded-lg font-bold hover:bg-emerald-600 transition-colors">SAVE</button>
                <button onClick={() => { setShowAddTeam(false); setIsVerified(false); }} className="flex-1 bg-slate-700 text-white py-3 rounded-lg font-bold hover:bg-slate-600 transition-colors">CANCEL</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-6 py-8 text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-emerald-500 text-white px-6 py-3 rounded-full font-bold text-sm hover:bg-emerald-600 transition-colors"
        >
          <i className="ri-home-line"></i>
          BACK TO HOME
        </Link>
      </div>
    </div>
  );
}
